import { BareButton } from '@calendly/ui/components/bare-button';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { mergeRefs } from '@calendly/ui/utils/merge-refs';
import classNames from 'classnames';
import { useRef, forwardRef, useState, useLayoutEffect, useMemo } from 'react';
import { useLeftNavigationState, useTextWidth } from './hooks';
import { useExpandPhaseContext } from './left_navigation_context';
const baseContainerStatePhoneCss = "b1ngakwk";
const baseContainerStatePhoneExpandedCss = "bnlgrdc";
const baseContainerStateDefaultCss = "bggvv52";
const baseContainerStateMinimizedCss = "bukyv0f";
const baseContainerStateExpandedCss = "b1hi1c7l";
const baseContainerCss = "bx30w11";

/**
 * Root container for the left nav; width and transition are driven by state.
 * @param {Object} props
 * @param {string} [props.state] - Gives CSS state to control width.
 */
export const BaseContainer = forwardRef((props, ref) => {
  const {
    state,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(baseContainerCss, {
    [baseContainerStatePhoneCss]: state?.toString() === 'phone',
    [baseContainerStatePhoneExpandedCss]: state?.toString() === 'phone-expanded',
    [baseContainerStateDefaultCss]: state?.toString() === 'default',
    [baseContainerStateMinimizedCss]: state?.toString() === 'minimized',
    [baseContainerStateExpandedCss]: state?.toString() === 'expanded'
  }, props.className)} />;
});
const navigationIconContainerCss = "ntces65";
export const NavigationIconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationIconContainerCss, props.className)} />;
});
const navigationLabelCss = "n2wybsz";
export const NavigationLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(navigationLabelCss, props.className)} />;
});
const titleCss = "t1piqhvi";
export const Title = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(titleCss, props.className)} />;
});
const subtitleCss = "suivsuk";
export const Subtitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subtitleCss, props.className)} />;
});
const wrapperStatePhoneCss = "w1xxx3t6";
const wrapperStateDefaultCss = "w19l5nla";
const wrapperStateMinimizedCss = "w1bi7z02";
const wrapperStateExpandedCss = "w1eelh80";
const wrapperStatePhoneExpandedCss = "w1av3mtd";
const wrapperCss = "wgzghvc";
export const Wrapper = forwardRef((props, ref) => {
  const {
    state,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'nav';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, {
    [wrapperStatePhoneCss]: state?.toString() === 'phone',
    [wrapperStateDefaultCss]: state?.toString() === 'default',
    [wrapperStateMinimizedCss]: state?.toString() === 'minimized',
    [wrapperStateExpandedCss]: state?.toString() === 'expanded',
    [wrapperStatePhoneExpandedCss]: state?.toString() === 'phone-expanded'
  }, props.className)} />;
});
const overlayNoBackgroundCss = "o1k81ni1";
const overlayCss = "ojrz821";
export const Overlay = forwardRef((props, ref) => {
  const {
    noBackground,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(overlayCss, {
    [overlayNoBackgroundCss]: noBackground
  }, props.className)} />;
});
const topWrapperCss = "tcx3egh";

/** Top bar padding when nav is minimized (collapsed). */
export const topWrapperMinimizedCss = "t5b6vy6";

/** Phone-only: top bar padding for mobile left nav menu. */
export const topWrapperPhoneCss = "tjfsgpv";

/** During collapse: keep logo left-aligned with consistent padding so the "C" doesn’t jump. */
export const topWrapperCollapsingCss = "t1l0y0jt";
export const TopWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(topWrapperCss, props.className)} />;
});
const toggleButtonIsPhoneCss = "t2epqcz";
const toggleButtonIsRelatedPositioningCss = "t14lgt0q";
const toggleButtonCss = "t1jztoua";
export const ToggleButton = forwardRef((props, ref) => {
  const {
    isRelatedPositioning,
    ...restProps
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <BareButton ref={ref} {...restProps} className={classNames(toggleButtonCss, {
    [toggleButtonIsPhoneCss]: isPhone,
    [toggleButtonIsRelatedPositioningCss]: isRelatedPositioning
  }, props.className)} />;
});
const globalCreateWrapperCss = "gz91nwe";
export const globalCreateWrapperMinimizedCss = "g10a98hy";

/** Wrapper around the Create button; use globalCreateWrapperMinimizedCss when nav is minimized. */
export const GlobalCreateWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(globalCreateWrapperCss, props.className)} />;
});
const navigationItemsCss = "n13u6av1";
export const NavigationItems = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationItemsCss, props.className)} />;
});
const navigationAnchorItemBaseIsMultilineCss = "n1vb3wl";
const navigationAnchorItemBaseIsMinimizedCss = "n1lmldd3";
const navigationAnchorItemBaseIsActiveCss = "ng5lpu0";
const navigationAnchorItemBaseIsHoveredCss = "nz5m5pm";
const navigationAnchorItemBaseIsCollapsingCss = "n1fqmonv";
const navigationAnchorItemBaseIsMinimizedLayoutCss = "nstuhj3";
const navigationAnchorItemBaseIsExpandingFadeOutCss = "nd6zm0x";
const navigationAnchorItemBaseIsExpandingFadeInPendingCss = "n1ao7m2y";
const navigationAnchorItemBaseIsExpandingFadeInCss = "n6xl302";
export const navigationLabelCaptionMinimizedCss = "nf4gkwg";

/* Shared fade-in: 250ms hold then 50ms fade (300ms total). Delay baked into keyframes so expand/collapse match. */

/* Fade-out duration so collapse/expand visibly fade to 0 (animation from 1→0, no prior frame needed). */
const NAV_ITEM_FADEOUT_MS = 150;

/* Minimized layout dimensions (shared so collapse/expand phases stay in sync). */
export const NAV_ITEM_MINIMIZED_MARGIN_H = 10;
/* Button (e.g. Help, Upgrade CTA) in collapsed state: 67px so 67 + 10 + 10 margin = 87px (88 - 1 border). */
export const NAV_ITEM_MINIMIZED_BUTTON_WIDTH_PX = 67;

/** Width/center for nav buttons in minimized layout only. Reused by NavigationAnchorItem (as=button) and UpgradeCTA. */
export const navButtonMinimizedWidthCss = "nopx8k5";

/** Class name only; layout lives inside navigationAnchorItemBaseCss so it overrides base styles when both apply. */
const navItemMinimizedLayoutSharedCss = "n1ixkcz9";

/* Nav item — hover colors as variables so :hover and .IsHoveredCss (parent-reported hover) share one definition */

const navigationAnchorItemBaseCss = "nvwzemk";

/** Delay (ms) before nav collapses; items animate fade out at full width, then width and minimized layout apply. */
export const COLLAPSE_FADEOUT_MS = NAV_ITEM_FADEOUT_MS;

/** Wrapper that applies the same phase fade as nav items. Use so the inner content (e.g. Upgrade CTA button) doesn't need phase opacity. */
const navItemFadeWrapperCss = "n1o6jz44";
export const NavItemFadeWrapper = ({
  children,
  isCollapsing,
  isMinimizedLayout,
  isExpandingFadeOut,
  isExpandingFadeInPending,
  isExpandingFadeIn
}) => <div className={classNames(navItemFadeWrapperCss, {
  [navigationAnchorItemBaseIsCollapsingCss]: isCollapsing,
  [navigationAnchorItemBaseIsMinimizedLayoutCss]: isMinimizedLayout,
  [navigationAnchorItemBaseIsExpandingFadeOutCss]: isExpandingFadeOut,
  [navigationAnchorItemBaseIsExpandingFadeInPendingCss]: isExpandingFadeInPending,
  [navigationAnchorItemBaseIsExpandingFadeInCss]: isExpandingFadeIn
})}>
    {children}
  </div>;
const NavigationAnchorItemBase = forwardRef((props, ref) => {
  const {
    isMultiline,
    isMinimized,
    isCollapsing,
    isExpandingFadeOut,
    isExpandingFadeInPending,
    isExpandingFadeIn,
    isActive,
    isHovered,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'a';
  /* When isExpandingFadeOut we use only isExpandingFadeOutCss for layout + fade-out; do NOT apply
   * isMinimizedLayoutCss or its fade-in animation would override the expand fade-out. */
  const isMinimizedLayout = isMinimized && !isCollapsing && !isExpandingFadeOut;
  const useMinimizedLayoutShared = isMinimizedLayout || isExpandingFadeOut || isExpandingFadeInPending;
  return <Component ref={ref} {...restProps} className={classNames(navigationAnchorItemBaseCss, {
    [navItemMinimizedLayoutSharedCss]: useMinimizedLayoutShared,
    [navButtonMinimizedWidthCss]: useMinimizedLayoutShared && AsComponent === BareButton,
    [navigationAnchorItemBaseIsMultilineCss]: isMultiline,
    [navigationAnchorItemBaseIsMinimizedCss]: isMinimized,
    [navigationAnchorItemBaseIsCollapsingCss]: isCollapsing,
    [navigationAnchorItemBaseIsExpandingFadeOutCss]: isExpandingFadeOut,
    [navigationAnchorItemBaseIsExpandingFadeInPendingCss]: isExpandingFadeInPending,
    [navigationAnchorItemBaseIsExpandingFadeInCss]: isExpandingFadeIn,
    [navigationAnchorItemBaseIsActiveCss]: isActive,
    [navigationAnchorItemBaseIsHoveredCss]: isHovered,
    [navigationAnchorItemBaseIsMinimizedLayoutCss]: isMinimizedLayout
  }, props.className)} />;
});

/**
 * Nav link (icon + label) with collapse behavior.
 * Fades out at full width, then uses minimized layout when width collapses.
 * On expand: fade out in minimized layout, then fade in in expanded layout.
 * Forwards ref to the anchor so parent can forward clicks from wrapper.
 *
 * @param {Object} props
 * @param {boolean} [props.isHovered] - When true, applies hover styles (used when parent wrapper is hovered)
 * @param {React.Ref<HTMLElement>|undefined|null} ref - Ref forwarded to the anchor (object or callback ref)
 */
export const NavigationAnchorItem = forwardRef((props, ref) => {
  const state = useLeftNavigationState();
  const {
    isExpanding,
    expandFadeOutDone,
    expandFadeInStarted
  } = useExpandPhaseContext();
  const internalRef = useRef(null);
  const mergedRef = useMemo(() => mergeRefs(internalRef, ref), [internalRef, ref]);
  const isMultiline = useTextWidth(internalRef, 175);
  const isMinimized = state === 'minimized';
  const [isCollapsing, setIsCollapsing] = useState(false);
  const isExpandingFadeOut = isExpanding && !expandFadeOutDone;
  const isExpandingFadeInPending = isExpanding && expandFadeOutDone && !expandFadeInStarted;
  const isExpandingFadeIn = isExpanding && expandFadeOutDone && expandFadeInStarted;

  /* useLayoutEffect so we set isCollapsing true before paint; otherwise first paint shows minimized layout and no fade-out */
  useLayoutEffect(() => {
    if (!isMinimized) {
      setIsCollapsing(false);
      return undefined;
    }
    setIsCollapsing(true);
    const t = setTimeout(() => setIsCollapsing(false), COLLAPSE_FADEOUT_MS);
    return () => clearTimeout(t);
  }, [isMinimized]);
  return <NavigationAnchorItemBase ref={mergedRef} {...props} isMultiline={isMultiline} isMinimized={isMinimized} isCollapsing={isCollapsing} isExpandingFadeOut={isExpandingFadeOut} isExpandingFadeInPending={isExpandingFadeInPending} isExpandingFadeIn={isExpandingFadeIn} />;
});
const navigationWrapperCss = "nw3boip";

/** Scrollable column that holds the main nav links (scheduling, contacts, etc.). */
export const NavigationWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationWrapperCss, props.className)} />;
});
const topNavigationCss = "twmaplx";
export const TopNavigation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(topNavigationCss, props.className)} />;
});
const bottomNavigationCss = "b167ofs6";
export const BottomNavigation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bottomNavigationCss, props.className)} />;
});
const loaderContainerCss = "l10utj7k";
export const LoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(loaderContainerCss, props.className)} />;
});

require("./elements.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./elements.js");